import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  List,
  ListItemText,
  ListSubheader,
  Typography,
  ListItemButton,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import Fab from '@mui/material/Fab';
import { Add, Close, MoreVert, DeleteOutline } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import InputBase from '@mui/material/InputBase';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'underscore';
import { i18next } from '@/config/i18n';
import { getTerritories, getTerritoryById, deleteTerritories } from '@/actions/territories/thunks';
import { toggleLoading } from '@/actions/common/commonPure';
import useLazyScroll from '@/components/Common/hooks/scrollHook';
import { AppDispatch, RootState } from '@/index';
import { Territory } from '@/config/types/Territories';
import { vehicleListStyles } from './styles';
import { setSelectedTerritory } from '@/actions/territories/pure';

type TerritoryListProps = {
  onMerge: (territory: any, name: string) => void;
  selected: Territory | undefined;
};

const PAGE_SIZE = 10;

export const TerritoryList: React.FC<TerritoryListProps> = ({ selected }) => {
  const {
    territories: { territory_request, territories, colors },
    core: { loading },
  } = useSelector((state: RootState) => state, isEqual);

  const [searchText, setSearchText] = useState('');
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // NEW: block lazy-scroll during delete/refresh to avoid “late” extra requests
  const [isMutating, setIsMutating] = useState(false);

  const classes = vehicleListStyles();
  const dispatch: AppDispatch = useDispatch();

  const onToggleFetching = useCallback((val: boolean) => dispatch(toggleLoading(val)), [dispatch]);

  const lazy_list = useRef<any>();
  const scrollEnd = useLazyScroll(lazy_list);

  // NOTE: This prevents extra pagination fetch while delete/refresh is happening
  useEffect(() => {
    if (
      scrollEnd &&
      !loading &&
      !isMutating &&
      territory_request.total_pages > territory_request.page
    ) {
      onToggleFetching(true);
      dispatch(getTerritories(true, PAGE_SIZE) as any).finally(() => onToggleFetching(false));
    }
  }, [scrollEnd, territory_request, loading, dispatch, onToggleFetching, isMutating]);

  function handleFilterChange({ target: { value } }: ChangeEvent<HTMLInputElement> | any) {
    setSearchText(value);
  }

  const filteredTerritories = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return territories;

    return territories.filter((t: any) => {
      const label = (t.code ?? t.name ?? t.id ?? '').toString().toLowerCase();
      return label.includes(q);
    });
  }, [territories, searchText]);

  const toggleChecked = (id: string) => {
    setCheckedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const handleDeleteSelected = () => {
    closeMenu();
    if (!checkedIds.length) return;
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    if (!checkedIds.length) return;

    setIsMutating(true);
    onToggleFetching(true);

    try {
      // Delete
      await dispatch(deleteTerritories(checkedIds) as any);

      // Clear selection immediately (prevents stale UI state)
      setCheckedIds([]);

      /**
       * Refresh list from scratch (page reset).
       * IMPORTANT: This assumes getTerritories(false, ...) resets pagination & replaces list.
       */
      await dispatch(getTerritories(false, PAGE_SIZE) as any);
    } catch (err) {
      console.error('Error deleting territories:', err);
    } finally {
      onToggleFetching(false);
      setIsMutating(false);
    }
  };

  const anySelected = checkedIds.length > 0;

  const handleAddTerritoryUI = () => {
    const tmpId = `tmp_${Date.now()}`;

    const blankTerritory: any = {
      id: tmpId,
      name: 'Gebietsname',
      code: '',
      identifier: '',
      postal_codes: [],
    };

    dispatch(setSelectedTerritory(blankTerritory));
  };

  // NEW: count logic (total when not searching, filtered count when searching)
  const displayCount = useMemo(() => {
    const q = searchText.trim();
    if (q) return filteredTerritories.length;

    // Prefer backend total if available; fallback to loaded length
    const totalFromApi = (territory_request as any)?.total;
    if (typeof totalFromApi === 'number') return totalFromApi;

    return territories.length;
  }, [searchText, filteredTerritories.length, territory_request, territories.length]);

  const displayCountLabel = useMemo(() => {
    // keeping your existing i18n plural logic
    return `${displayCount} ${i18next.t('common:areaEditor.areas', {
      multi: displayCount !== 1 ? (i18next.language === 'de' ? 'e' : 's') : '',
    })}`;
  }, [displayCount]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            disableTypography
            title={
              <Typography color="inherit" variant="h5">
                {i18next.t('common:areaEditor.areas')}
              </Typography>
            }
            subheader={<Typography variant="subtitle1">{displayCountLabel}</Typography>}
            action={
              anySelected ? (
                <>
                  <IconButton
                    onClick={openMenu}
                    size="large"
                    aria-label="more actions"
                    id="territory-more"
                    disabled={loading || isMutating}
                  >
                    <MoreVert color={'primary'} />
                  </IconButton>

                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={closeMenu}
                    keepMounted
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem
                      onClick={handleDeleteSelected}
                      id="territory-delete"
                      disabled={loading || isMutating}
                    >
                      <ListItemIcon>
                        <DeleteOutline fontSize="small" />
                      </ListItemIcon>
                      {`${i18next.t('common:buttons.delete')}`}
                    </MenuItem>
                  </Menu>
                </>
              ) : null
            }
          />

          <Divider />

          <CardContent className={classes.cardContent} ref={lazy_list}>
            <List disablePadding id="territory-list">
              <ListSubheader component="div" className={classes.stickyListHeader}>
                <div className={`${classes.search} ${classes.searchPadding}`}>
                  <InputBase
                    disabled={loading || isMutating}
                    onChange={handleFilterChange}
                    value={searchText}
                    placeholder={`${i18next.t('common:areaEditor.search')}...`}
                    endAdornment={
                      !!searchText.length && (
                        <Close
                          color="inherit"
                          fontSize="small"
                          className={classes.clearIcon}
                          onClick={() => handleFilterChange({ target: { value: '' } })}
                        />
                      )
                    }
                    startAdornment={<SearchIcon color="inherit" />}
                    classes={{
                      root: classes.inputRoot,
                      input: `${classes.inputInput}, ${loading ? classes.notAllowed : ''}`,
                    }}
                    inputProps={{ 'aria-label': 'search', id: 'territory-search' }}
                  />
                </div>
              </ListSubheader>

              {filteredTerritories.map((t: any) => {
                const primary = t.code ?? t.name ?? t.id;
                const secondary = '';
                const isChecked = checkedIds.includes(t.id);

                return (
                  <ListItemButton
                    key={t.id}
                    divider
                    onClick={() => dispatch(getTerritoryById(t.id) as any)}
                    selected={Boolean(selected && selected.id === t.id)}
                    sx={{ display: 'flex', alignItems: 'stretch' }}
                    disabled={isMutating}
                  >
                    <span className={classes.bar} style={{ backgroundColor: colors?.[t.id]?.color }} />

                    <ListItemText
                      disableTypography
                      primary={<Typography variant="body1">{primary}</Typography>}
                      secondary={
                        <Typography variant="body2" color="textSecondary">
                          {secondary}
                        </Typography>
                      }
                    />

                    <Checkbox
                      edge="end"
                      checked={isChecked}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleChecked(t.id);
                      }}
                      inputProps={{ 'aria-label': 'territory checkbox' }}
                      sx={{ p: 2 }}
                      disabled={isMutating}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </CardContent>

          <CardActions>
            <Grid container alignContent="flex-end" alignItems="center" justifyContent="flex-end">
              <Fab
                color="primary"
                aria-label="add"
                onClick={handleAddTerritoryUI}
                size="small"
                id="add-territory"
                disabled={loading || isMutating}
              >
                <Add color={'bright'} />
              </Fab>
            </Grid>
          </CardActions>
        </Card>

        <Dialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
          classes={{ paper: classes.dialogPaper }}
        >
          <DialogTitle className={classes.titleRoot}>
            <div className={classes.titleRow}>
              <div className={classes.iconWrap}>
                <DeleteOutline fontSize="medium" className={classes.deleteIcon} />
              </div>

              <div className={classes.titleText}>{i18next.t('common:areaEditor.deleteTerritory')}</div>

              <IconButton className={classes.closeBtn} onClick={() => setConfirmOpen(false)} size="small">
                <Close />
              </IconButton>
            </div>
          </DialogTitle>

          <DialogContent>
            <Typography className={classes.messageText}>
              {i18next.t('common:areaEditor.deleteConfirmMsg')}
            </Typography>
          </DialogContent>

          <DialogActions className={classes.actionsRoot}>
            <Button
              onClick={() => setConfirmOpen(false)}
              variant="outlined"
              className={classes.cancelBtn}
              disabled={loading || isMutating}
            >
              {i18next.t('common:buttons.cancel')}
            </Button>

            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="warning"
              className={classes.deleteBtn}
              autoFocus
              disabled={loading || isMutating}
            >
              {i18next.t('common:buttons.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Grid>
  );
};
